import { Box, Checkbox, Flex, Table, Tbody, Thead, Tooltip, Tr } from '@chakra-ui/react';
import type { ReactElement } from 'react';
import type { LocationEntry } from '../../../../hooks/useGetLocations';
import { SettingsTd, SettingsTh } from '../../../../components/settings/primitives';
import { SETTINGS_BORDER_COLOR } from '../../../../components/settings/primitives/constants';
import {
    VENDOR_PREFERENCE_COLUMNS,
    isAllChecked,
    isLocationChecked,
    setAllPreference,
    setLocationPreference,
    type VendorPreferenceKey,
    type VendorPreferences,
} from './vendorPreferences';

export interface VendorPreferencesTableProps {
    locations: LocationEntry[];
    preferences: VendorPreferences;
    onChange: (next: VendorPreferences) => void;
    isDisabled?: boolean;
    /** Columns that stay visible but cannot be selected (e.g. Required for non-dealers). */
    disabledKeys?: Array<VendorPreferenceKey>;
    disabledReason?: string;
}

/**
 * Preferences by location: one row per shop, one column per category, and an
 * "All Locations" row that applies a category org-wide. A location belongs to at
 * most one category, so checking a box clears the others in that row.
 *
 * A contained table rather than a full-bleed one — it sits inside a pane, so it
 * keeps its own border and radius, and its height follows its rows rather than
 * stretching, so a short list leaves no empty band under the last row. Once the
 * rows would outgrow the pane the body scrolls under a sticky header instead.
 */
const VendorPreferencesTable = ({
    locations,
    preferences,
    onChange,
    isDisabled = false,
    disabledKeys = [],
    disabledReason,
}: VendorPreferencesTableProps) => {
    const allLocationIds = locations.map((location) => location._id as string);
    const locked = new Set(disabledKeys);

    const wrapCheckbox = (key: VendorPreferenceKey, checkbox: ReactElement) => {
        if (!locked.has(key) || !disabledReason) return checkbox;
        return (
            <Tooltip label={disabledReason} placement="top" hasArrow>
                <Box>{checkbox}</Box>
            </Tooltip>
        );
    };

    return (
        <Flex
            direction="column"
            minH={0}
            maxH="100%"
            bg="white"
            borderWidth="1px"
            borderColor={SETTINGS_BORDER_COLOR}
            borderRadius="lg"
            overflow="hidden"
        >
            <Box
                minH={0}
                overflow="auto"
                sx={{
                    '& thead': { zIndex: 2 },
                    '& th': {
                        bg: 'gray.50',
                        borderBottomWidth: '1px',
                        borderBottomColor: 'var(--chakra-colors-gray-200)',
                    },
                    // Column dividers, but never on the table's outer edges.
                    '& th:not(:last-of-type), & td:not(:last-of-type)': {
                        borderRightWidth: '1px',
                        borderRightStyle: 'solid',
                        borderRightColor: 'var(--chakra-colors-gray-100)',
                    },
                    '& tbody tr:last-of-type td': { borderBottomWidth: 0 },
                    '& th:first-of-type, & td:first-of-type': { pl: 5 },
                    '& th:last-of-type, & td:last-of-type': { pr: 5 },
                    '&::-webkit-scrollbar': { width: '8px', height: '8px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { background: '#cbd5e0', borderRadius: '4px' },
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#cbd5e0 transparent',
                }}
            >
                <Table variant="simple" size="sm" w="100%" sx={{ tableLayout: 'fixed' }}>
                    <Thead position="sticky" top={0}>
                        <Tr bg="gray.50">
                            <SettingsTh w="26%">LOCATION</SettingsTh>
                            {VENDOR_PREFERENCE_COLUMNS.map((column) => (
                                <SettingsTh key={column.key} textAlign="center">
                                    {column.label}
                                </SettingsTh>
                            ))}
                        </Tr>
                    </Thead>
                    <Tbody>
                        {/* Org-wide row, tinted so it reads as a control over
                            the rows below it. */}
                        <Tr bg="gray.50">
                            <SettingsTd fontWeight="semibold" color="gray.800">
                                All Locations
                            </SettingsTd>
                            {VENDOR_PREFERENCE_COLUMNS.map((column) => (
                                <SettingsTd key={column.key} textAlign="center">
                                    <Flex justify="center">
                                        {wrapCheckbox(
                                            column.key,
                                            <Checkbox
                                                colorScheme="partbay-blue"
                                                isChecked={isAllChecked(preferences[column.key])}
                                                isDisabled={isDisabled || locked.has(column.key)}
                                                onChange={(e) =>
                                                    onChange(
                                                        setAllPreference(
                                                            preferences,
                                                            column.key,
                                                            e.target.checked,
                                                        ),
                                                    )
                                                }
                                                aria-label={`${column.label} for all locations`}
                                            />,
                                        )}
                                    </Flex>
                                </SettingsTd>
                            ))}
                        </Tr>

                        {locations.map((location) => (
                            <Tr key={location._id} _hover={{ bg: 'gray.25' }}>
                                <SettingsTd color="gray.700">{location.title}</SettingsTd>
                                {VENDOR_PREFERENCE_COLUMNS.map((column) => (
                                    <SettingsTd key={column.key} textAlign="center">
                                        <Flex justify="center">
                                            {wrapCheckbox(
                                                column.key,
                                                <Checkbox
                                                    colorScheme="partbay-blue"
                                                    isChecked={isLocationChecked(
                                                        preferences[column.key],
                                                        location._id as string,
                                                    )}
                                                    isDisabled={isDisabled || locked.has(column.key)}
                                                    onChange={(e) =>
                                                        onChange(
                                                            setLocationPreference(
                                                                preferences,
                                                                column.key,
                                                                location._id as string,
                                                                e.target.checked,
                                                                allLocationIds,
                                                            ),
                                                        )
                                                    }
                                                    aria-label={`${column.label} — ${location.title}`}
                                                />,
                                            )}
                                        </Flex>
                                    </SettingsTd>
                                ))}
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>
        </Flex>
    );
};

export default VendorPreferencesTable;
